import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { parseCourtDateCalendarKey, formatCourtDateDisplayDay } from '../../../common/dateUtils';
import axiosInstance from '../../../fetchConfig/axiosConfig';
import logger from '../../../utils/logger';

const toStartOfDay = (value) => {
  if (dayjs.isDayjs(value) && value.isValid()) return value.startOf('day');
  if (value instanceof Date && !isNaN(value.getTime())) {
    return dayjs(new Date(value.getFullYear(), value.getMonth(), value.getDate()));
  }
  const key = parseCourtDateCalendarKey(value);
  if (!key) return null;
  const [y, m, d] = key.split('-').map(Number);
  return dayjs(new Date(y, m - 1, d));
};

const rarHistoryByMuni = new Map();

export const fetchRarHistory = (muniCode) => {
  if (!muniCode) return Promise.resolve([]);
  const cached = rarHistoryByMuni.get(muniCode);
  if (Array.isArray(cached)) return Promise.resolve(cached);
  if (cached?.then) return cached;
  const request = axiosInstance.post('/rar-history', { MuniCode: muniCode })
    .then((response) => {
      const data = response.data?.success ? (response.data.rar_history || []) : [];
      rarHistoryByMuni.set(muniCode, data);
      return data;
    })
    .catch((err) => {
      rarHistoryByMuni.delete(muniCode);
      logger.error('[useRarHistory] Failed to load RAR history', err);
      throw err;
    });
  rarHistoryByMuni.set(muniCode, request);
  return request;
};

export const useRarHistory = (muniCode) => {
  const cached = rarHistoryByMuni.get(muniCode);
  const [rarHistory, setRarHistory] = useState(() => (Array.isArray(cached) ? cached : []));
  useEffect(() => {
    if (!muniCode) {
      setRarHistory([]);
      return;
    }
    const existing = rarHistoryByMuni.get(muniCode);
    if (Array.isArray(existing)) {
      setRarHistory(existing);
      return;
    }
    let cancelled = false;
    fetchRarHistory(muniCode)
      .then((data) => { if (!cancelled) setRarHistory(data); })
      .catch(() => { if (!cancelled) setRarHistory([]); });
    return () => { cancelled = true; };
  }, [muniCode]);
  return rarHistory;
};

/** Windows are (eval + n years, eval + n+1 years]; n=-1 is the prior 1 year of sales from eval. */
export const getRarYearForSale = (saleDate, evalDate, taxYear) => {
  const sale = toStartOfDay(saleDate);
  const evalD = toStartOfDay(evalDate);
  if (!sale || !evalD) return null;
  let n = sale.year() - evalD.year();
  if (!sale.isAfter(evalD.year(sale.year()))) n -= 1;
  const base = Number(taxYear);
  return (Number.isFinite(base) && base > 0 ? base + 1 : evalD.year() + 2) + n;
};

const rarEntryForYear = (rarHistory, year) => {
  if (!Array.isArray(rarHistory) || year == null) return null;
  const row = rarHistory.find((r) => Number(r.Year) === Number(year));
  if (!row) return null;
  if (row.RAR != null && Number.isFinite(Number(row.RAR))) {
    return { rar: Number(row.RAR), isProjected: false };
  }
  if (row.ProjectedRAR != null && Number.isFinite(Number(row.ProjectedRAR))) {
    return { rar: Number(row.ProjectedRAR), isProjected: true };
  }
  return { rar: null, isProjected: false };
};

export const calculateCalculatedTav = ({ salePrice, saleDate, evalDate, taxYear, rarHistory }) => {
  const price = Number(String(salePrice ?? '').replace(/[,$]/g, ''));
  const sale = toStartOfDay(saleDate);
  const evalD = toStartOfDay(evalDate);
  const rarYear = sale && evalD ? getRarYearForSale(saleDate, evalDate, taxYear) : null;
  const entry = rarYear != null ? rarEntryForYear(rarHistory, rarYear) : null;
  const missing = [];
  if (!Number.isFinite(price) || price <= 0) missing.push('sale price');
  if (!sale) missing.push('sale date');
  if (!evalD) missing.push('eval date');
  if (!missing.length && rarYear != null && !entry) missing.push(`RAR for ${rarYear}`);
  if (!missing.length && entry && entry.rar == null) missing.push(`RAR value for ${rarYear}`);
  const rar = entry?.rar ?? null;
  const tav = !missing.length && rar != null ? Math.round(price * rar) : null;
  return {
    tav,
    price: Number.isFinite(price) && price > 0 ? price : null,
    saleDateLabel: sale ? formatCourtDateDisplayDay(sale.format('YYYY-MM-DD')) : (saleDate || '—'),
    evalDateLabel: evalD ? formatCourtDateDisplayDay(evalD.format('YYYY-MM-DD')) : (evalDate || '—'),
    rarYear,
    rar,
    isProjected: Boolean(entry?.isProjected),
    missing,
  };
};
